import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';

import {
  AccountAppDetails,
  AccountUse,
  Application,
  AppListRecord,
  AppStatusEnum,
  isAllowedLimitChange,
  isAllowedOwnLicense,
} from 'api-bible-shared-lib/utils/types';
import {
  planCountLimitValidator,
  dblTokenValidator,
  authorizedBiblesValidator,
} from 'api-bible-shared-lib/utils/validation';

import Modal from './Modal';
import { Alert, AlertType } from './Alert';
import { Button } from './Button';
import { IconKey } from './Icons';
import { InputGroup } from './Inputs';
import Pill from './Pill';

import { toLocaleDateTimeString } from '../utils/time';
import * as portalApi from '../utils/api';

interface ApplicationEditModalProps {
  appListRecord: AppListRecord;
  handleCancel: () => void;
}

interface FormState {
  dailyLimit: number;
  monthlyLimit: number;
  dblToken: string;
  authorizedBiblesStringList: string;
}

const validationSchema = yup.object().shape({
  dailyLimit: planCountLimitValidator.clone().required().label('Daily Limit'),
  monthlyLimit: planCountLimitValidator.clone().required().label('Monthly Limit'),
  dblToken: dblTokenValidator.clone(),
  authorizedBiblesStringList: authorizedBiblesValidator.clone(),
});

export default function ApplicationEditModal({
  appListRecord,
  handleCancel,
}: ApplicationEditModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [success, setSuccess] = useState<string | undefined>();
  const [accountApp, setAccountApp] = useState<AccountAppDetails>();

  const { accountId, appId } = appListRecord;
  useEffect(() => {
    portalApi
      .getSuperAdminAccountApp(accountId, appId)
      .then((accountAppDetails) => setAccountApp(accountAppDetails))
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [accountId, appId]);

  if (!accountApp) return null;

  const allowLimitChange = isAllowedLimitChange(accountApp.app.plan);
  const allowLicenseChange = isAllowedOwnLicense(accountApp.app.plan);

  const authorizedBiblesStringList =
    accountApp && accountApp.app.authorizedBibles ? accountApp.app.authorizedBibles.join() : '';
  const initialValues: FormState = {
    dailyLimit: accountApp.app.dailyLimit ?? 0,
    monthlyLimit: accountApp.app.monthlyLimit ?? 0,
    dblToken: accountApp.app.dblToken ?? '',
    authorizedBiblesStringList,
  };

  const LineItem = ({
    label = '',
    span = '1',
    children,
  }: {
    label: string;
    children: React.ReactNode;
    span?: string;
  }) => (
    <div className={`sm:col-span-${span}`}>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );

  const onSubmit = async (values: FormState, { setSubmitting }: any) => {
    setSuccess(undefined);
    setError(undefined);

    const newData: Partial<Application> = {};
    if (allowLimitChange) {
      newData.dailyLimit = values.dailyLimit;
      newData.monthlyLimit = values.monthlyLimit;
    }
    if (allowLicenseChange) {
      newData.dblToken = values.dblToken;
      newData.authorizedBibles = values.authorizedBiblesStringList.split(',');
    }

    try {
      await portalApi.patchSuperAdminAccountApp({
        accountId,
        appId,
        newData,
      });
      setSuccess('Successfully updated.');
    } catch (e) {
      const err = e as Error;
      setError(err);
    }

    setSubmitting(false);
  };

  return (
    <Modal
      className="w-full md:max-w-screen-md xl:max-w-screen-lg"
      body={
        loading ? (
          <div className="h-96">
            <p>Loading...</p>
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                {success && (
                  <Alert
                    className="mb-8"
                    type={AlertType.SUCCESS}
                    title="Success!"
                    message={success}
                  />
                )}
                {error && (
                  <Alert
                    className="mb-8"
                    type={AlertType.ERROR}
                    title="There was an error with your submission"
                    message={error?.message || 'An unexpected error occurred.'}
                  />
                )}

                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {accountApp.app.name}
                    </h3>
                    <div className="flex items-center">
                      <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        <IconKey className="inline w-5 h-5 mr-2.5" />
                        {accountApp.app.apiKey}
                        {!accountApp.app.isActiveKey && (
                          <Pill className="text-black inline-block ml-2 -mb-px" color="gray">
                            Paused
                          </Pill>
                        )}
                        {accountApp.app.status !== AppStatusEnum.ACTIVE && (
                          <Pill className="text-black inline-block ml-2 -mb-px" color="red">
                            {accountApp.app.status.toUpperCase()}
                          </Pill>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <dl className="grid gap-x-4 gap-y-8 sm:grid-cols-5 ">
                      <LineItem label="Admin Name">{appListRecord.userName}</LineItem>
                      <LineItem label="Email" span="4">
                        {appListRecord.userEmail}
                      </LineItem>
                      <LineItem label="Account ID">{accountApp.account.id}</LineItem>
                      <LineItem label="Account Name / Type">
                        {accountApp.account.name} / {accountApp.account.type}
                      </LineItem>
                      <LineItem label="Use">
                        {accountApp.account.use === AccountUse.other
                          ? accountApp.account.useOther
                          : accountApp.account.use}
                      </LineItem>
                      <LineItem label="Website">{accountApp.account.website}</LineItem>
                      <LineItem label="Created At">
                        {toLocaleDateTimeString(accountApp.account?.createdAt)}
                      </LineItem>

                      <LineItem label="Application ID">{accountApp.app.id}</LineItem>
                      <LineItem label="Appliction Name / Type">
                        {accountApp.app.name} / {accountApp.app.type}
                      </LineItem>
                      <LineItem label="Description">{accountApp!.app.description}</LineItem>
                      <LineItem label="Marketing URL">{accountApp.app.marketingUrl}</LineItem>
                      <LineItem label="Created At">
                        {toLocaleDateTimeString(accountApp.app.createdAt)}
                      </LineItem>

                      <LineItem label=" "> </LineItem>
                      <LineItem label="Plan">{accountApp.app.plan}</LineItem>
                      {!allowLimitChange && (
                        <>
                          <LineItem label="Daily Count / Limit">
                            {accountApp?.apiKeyCounts.dailyCount || '0'} {' / '}
                            {accountApp.app.dailyLimit || 'Unlimited'}
                          </LineItem>
                          <LineItem label="Monthly Count / Limit">
                            {accountApp?.apiKeyCounts.monthlyCount || '0'} {' / '}
                            {accountApp.app.monthlyLimit || 'Unlimited'}
                          </LineItem>
                        </>
                      )}
                      {allowLimitChange && (
                        <>
                          <LineItem label="Daily Count / Limit">
                            <div className="flex">
                              <div className="mt-3 flex-1">
                                {accountApp?.apiKeyCounts.dailyCount || '0'}
                              </div>
                              <div className="mt-3 flex-1">/</div>
                              <div className="flex-1">
                                <InputGroup name="dailyLimit" type="number" />
                              </div>
                            </div>
                          </LineItem>
                          <LineItem label="Monthly Count / Limit">
                            <div className="flex">
                              <div className="mt-3 flex-1">
                                {accountApp?.apiKeyCounts.monthlyCount || '0'}
                              </div>
                              <div className="mt-3 flex-1">/</div>
                              <div className="flex-1">
                                <InputGroup name="monthlyLimit" type="number" />
                              </div>
                            </div>
                          </LineItem>
                        </>
                      )}
                      <LineItem label="Count Last Updated At">
                        {toLocaleDateTimeString(accountApp?.apiKeyCounts.countUpdatedAt)}
                      </LineItem>
                      {accountApp.app.stripeSubscriptionId && (
                        <>
                          <LineItem label=" "> </LineItem>
                          <LineItem label="Billing Anchor">
                            {toLocaleDateTimeString(accountApp.app.billingAnchorAt)}
                          </LineItem>
                          <LineItem label="Current Billing Start">
                            {toLocaleDateTimeString(accountApp.app.billingStartAt)}
                          </LineItem>
                          <LineItem label="Current Billing End">
                            {toLocaleDateTimeString(accountApp.app.billingEndAt)}
                          </LineItem>
                          <LineItem label="Stripe Subscription ID">
                            {accountApp.app.stripeSubscriptionId}
                          </LineItem>
                        </>
                      )}

                      <LineItem label=" "> </LineItem>
                      <LineItem label="DBL token" span="2">
                        {allowLicenseChange && (
                          <InputGroup name="dblToken" placeholder="20 character DBL access token" />
                        )}
                        {!allowLicenseChange && accountApp.app.dblToken}
                      </LineItem>
                      <LineItem label="Authorized Bibles" span="2">
                        {allowLicenseChange && (
                          <InputGroup
                            name="authorizedBiblesStringList"
                            as="textarea"
                            rows="3"
                            placeholder="A comma-delimited list of 16 character Bible IDs"
                          />
                        )}
                        {!allowLicenseChange && authorizedBiblesStringList}
                      </LineItem>
                    </dl>
                  </div>
                </div>

                <div className="w-full pt-6 mt-8">
                  <div className="flex justify-end">
                    <Button type="button" outline className="mr-2" onClick={handleCancel}>
                      Dismiss
                    </Button>
                    {(allowLimitChange || allowLicenseChange) && (
                      <Button outline disabled={isSubmitting} loading={isSubmitting} type="submit">
                        Save Changes
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            )}
          </Formik>
        )
      }
    />
  );
}
